import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { UploadCloud, FileText, CheckCircle2, XCircle, Clock, Loader2, ExternalLink } from "lucide-react";
import { uploadDocument } from "@/lib/portalApi";
import { DOC_STATUS_STYLES, fileHref } from "./accountConstants";
import { toast } from "sonner";

const ALLOWED = ["application/pdf", "image/png", "image/jpeg", "image/jpg"];
const MAX_BYTES = 10 * 1024 * 1024;

const STATUS_ICON = {
  required: Clock,
  uploaded: Loader2,
  approved: CheckCircle2,
  rejected: XCircle,
};

const DocumentRow = ({ doc, onUploaded }) => {
  const { t } = useTranslation();
  const inputRef = useRef(null);
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);

  const canUpload = doc.kind !== "produced" && doc.status !== "approved";
  const produced = doc.kind === "produced";
  const Icon = STATUS_ICON[doc.status] || Clock;
  const href = fileHref(doc.file_url);

  const doUpload = async (file) => {
    if (!file) return;
    if (!ALLOWED.includes((file.type || "").toLowerCase())) {
      toast.error(t("account.errors.fileType"));
      return;
    }
    if (file.size > MAX_BYTES) {
      toast.error(t("account.errors.fileSize"));
      return;
    }
    setUploading(true);
    try {
      const updated = await uploadDocument(doc.id, file);
      onUploaded(updated);
      toast.success(doc.title);
    } catch (e) {
      toast.error(t("account.errors.uploadFailed"));
    } finally {
      setUploading(false);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    if (!canUpload) return;
    const file = e.dataTransfer.files?.[0];
    doUpload(file);
  };

  return (
    <div
      className="rounded-xl border border-slate-800 bg-slate-900/40 p-4"
      data-testid={`vault-doc-${doc.id}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <FileText className="h-4 w-4 text-slate-400 shrink-0" />
          <span className="font-medium break-words" title={doc.title}>{doc.title}</span>
          {produced && (
            <span className="rounded-full bg-venka-blue/20 text-venka-green-light px-2 py-0.5 text-[10px] font-semibold uppercase shrink-0">
              {t("account.vault.fromVenka", "From VENKA")}
            </span>
          )}
        </div>
        <span
          className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-[11px] font-semibold whitespace-nowrap ${DOC_STATUS_STYLES[doc.status]}`}
          data-testid={`vault-status-${doc.id}`}
        >
          <Icon className="h-3 w-3" />
          {t(`account.docStatus.${doc.status}`)}
        </span>
      </div>

      {doc.status === "rejected" && doc.rejection_reason && (
        <p className="mt-2 text-xs text-red-400">
          {t("account.vault.rejectionReason")}: {doc.rejection_reason}
        </p>
      )}

      {href && (
        <a
          href={href}
          target="_blank"
          rel="noreferrer"
          className="mt-2 inline-flex items-center gap-1 text-xs text-venka-green-light hover:underline"
          data-testid={`vault-view-${doc.id}`}
        >
          <ExternalLink className="h-3 w-3" /> {t("account.vault.viewFile")}
          {doc.file_name ? ` (${doc.file_name})` : ""}
        </a>
      )}

      {canUpload && (
        <div
          onClick={() => !uploading && inputRef.current?.click()}
          onDragOver={(e) => {
            e.preventDefault();
            setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={onDrop}
          className={`mt-3 cursor-pointer rounded-lg border border-dashed p-4 text-center transition-colors ${
            dragging ? "border-venka-green-light bg-venka-green-light/5" : "border-slate-700 hover:border-slate-500"
          }`}
          data-testid={`vault-dropzone-${doc.id}`}
        >
          <input
            ref={inputRef}
            type="file"
            accept=".pdf,.png,.jpg,.jpeg"
            className="hidden"
            onChange={(e) => doUpload(e.target.files?.[0])}
            data-testid={`vault-input-${doc.id}`}
          />
          {uploading ? (
            <span className="flex items-center justify-center gap-2 text-sm text-slate-300">
              <Loader2 className="h-4 w-4 animate-spin" /> {t("account.vault.uploading")}
            </span>
          ) : (
            <div className="flex flex-col items-center gap-1">
              <UploadCloud className="h-5 w-5 text-slate-400" />
              <span className="text-sm text-slate-300">
                {doc.status === "rejected" ? t("account.vault.reupload") : t("account.vault.dropHere")}
              </span>
              <span className="text-[11px] text-slate-500">
                {t("account.vault.allowed")} · {t("account.vault.maxSize")}
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export const DocumentVault = ({ documents, onChange }) => {
  const { t } = useTranslation();

  const handleUploaded = (updated) => {
    onChange((docs) => docs.map((d) => (d.id === updated.id ? updated : d)));
  };

  return (
    <section className="rounded-2xl border border-slate-800 bg-slate-900/40 p-5" data-testid="document-vault">
      <h2 className="font-semibold">{t("account.vault.title")}</h2>
      <p className="text-sm text-slate-400 mt-1 mb-4">{t("account.vault.subtitle")}</p>
      <div className="grid gap-3 sm:grid-cols-2">
        {documents.map((doc) => (
          <DocumentRow key={doc.id} doc={doc} onUploaded={handleUploaded} />
        ))}
      </div>
    </section>
  );
};

export default DocumentVault;
